import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, logout: async () => {}, session: null });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user from backend after session is established
  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Listen to auth state changes from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      if (session?.user) {
        // Set JWT token in API headers - on every event, including
        // TOKEN_REFRESHED, or calls keep sending the expired token and 401
        // about an hour into a session.
        if (session.access_token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        }
        await checkAuth();
      } else {
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
        setLoading(false);
      }
    });

    // Check if already authenticated on initial load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user && session.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        checkAuth();
      } else {
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, session, refresh: checkAuth, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
